import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Hyperliquid formatting constants
const MAX_PERP_DECIMALS = 6;
const MAX_SPOT_DECIMALS = 8;
const MAX_SIGNIFICANT_FIGURES = 5;

/**
 * Format a price according to Hyperliquid's rules
 * - Prices can have up to 5 significant figures
 * - Maximum decimal places = MAX_DECIMALS - szDecimals
 * - Integer prices are always allowed
 *
 * @param price - The price to format (string or number)
 * @param szDecimals - The size decimals for the asset (from meta response)
 * @param isSpot - Whether this is for spot trading (default: false for perp)
 * @returns Formatted price string
 */
export function formatPrice(price: string | number, szDecimals: number, isSpot: boolean = false): string {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(numPrice) || numPrice <= 0) {
    return '0';
  }

  const maxDecimals = (isSpot ? MAX_SPOT_DECIMALS : MAX_PERP_DECIMALS) - szDecimals;

  // If it's an integer, return as is
  if (Number.isInteger(numPrice)) {
    return numPrice.toString();
  }

  // Check significant figures first
  const priceStr = numPrice.toString();
  const [integerPart, decimalPart = ''] = priceStr.split('.');

  // Count significant figures
  let significantDigits = 0;
  let result = '';

  // Count non-zero digits in integer part
  const integerDigits = integerPart.replace(/^0+/, ''); // Remove leading zeros
  if (integerDigits) {
    significantDigits += integerDigits.length;
    result += integerPart;
  }

  // If we haven't reached 5 significant figures and have decimal part
  if (significantDigits < MAX_SIGNIFICANT_FIGURES && decimalPart) {
    result += '.';

    let decimalIndex = 0;
    for (const digit of decimalPart) {
      if (significantDigits >= MAX_SIGNIFICANT_FIGURES || decimalIndex >= maxDecimals) {
        break;
      }

      result += digit;
      decimalIndex++;

      // Count significant digits (including leading zeros in decimal if no integer part)
      if (digit !== '0' || significantDigits > 0 || integerDigits) {
        significantDigits++;
      }
    }
  }

  // Remove trailing zeros and decimal point if not needed
  if (result.includes('.')) {
    result = result.replace(/\.?0+$/, '');
  }

  return result || '0';
}

/**
 * Format a size according to Hyperliquid's rules
 * - Sizes are rounded to szDecimals for that specific asset
 *
 * @param size - The size to format (string or number)
 * @param szDecimals - The size decimals for the asset (from meta response)
 * @returns Formatted size string
 */
export function formatSize(size: string | number, szDecimals: number): string {
  const numSize = typeof size === 'string' ? parseFloat(size) : size;

  if (isNaN(numSize) || numSize <= 0) {
    return '0';
  }

  return numSize.toFixed(szDecimals);
}

/**
 * Get price decimals for an asset
 * @param szDecimals - The size decimals for the asset
 * @param isSpot - Whether this is for spot trading (default: false for perp)
 * @returns Number of decimal places allowed for prices
 */
export function getPriceDecimals(szDecimals: number, isSpot: boolean = false): number {
  return (isSpot ? MAX_SPOT_DECIMALS : MAX_PERP_DECIMALS) - szDecimals;
}
